import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Constants, isLotteryDrawOpen } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Pagination from "src/components/Pagination/Pagination";
import SegmentedControl from "src/components/SegmentedControl/SegmentedControl";
import { CancelRenderer } from "./CancelRenderer";

import type { IUser } from "@eagerdog/interfaces";

import styles from "./UserWaitlistTab.module.scss";

interface IProps {
  user: IUser;
}

const ALL_DOGS: IOption = { id: "", value: "All dogs" };
const ALL_STATUSES: IOption = { id: "", value: "All statuses" };
const ALL_EVENTS: IOption = { id: "", value: "All events and courses" };
const PAGE_SIZES = [5, 10, 20];

const STATUS_LABELS: Record<string, string> = {
  [Constants.waitlist_status.registered]: "Registered",
  [Constants.waitlist_status.waitlist]: "Waitlisted",
  awaiting_draw: "Awaiting Draw",
  [Constants.waitlist_status.awaiting_payment]: "Awaiting Payment",
  [Constants.waitlist_status.pending_refund]: "Pending Refund",
  [Constants.waitlist_status.refund_denied]: "Refund Denied",
  [Constants.waitlist_status.refunded]: "Refunded",
  [Constants.waitlist_status.canceled]: "Canceled",
  [Constants.waitlist_status.declined]: "Declined",
};

const emptyPage: apiService.IUserEntriesPage = { items: [], total: 0, dogs: [], events: [], statuses: [] };

function CalendarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2v4M18 2v4M3 10h18" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
    </svg>
  );
}

function sessionLine(session: { index?: number; start_date?: string }): string {
  const when = session.start_date ? moment(session.start_date).format("MMM D, YYYY, h:mm A") : "";
  if (session.index && session.index > 0) return `Session ${session.index} · ${when}`;
  return when;
}

function statusLabel(row: any): { label: string; tone: string } {
  const awaitingDraw = row.status === Constants.waitlist_status.waitlist && isLotteryDrawOpen(row.event);
  if (awaitingDraw) return { label: "Awaiting Draw", tone: "pending" };
  if (row.status === Constants.waitlist_status.registered) return { label: STATUS_LABELS[row.status], tone: "registered" };
  if (row.status === Constants.waitlist_status.waitlist) return { label: STATUS_LABELS[row.status], tone: "waitlist" };
  if (
    row.status === Constants.waitlist_status.refunded ||
    row.status === Constants.waitlist_status.canceled ||
    row.status === Constants.waitlist_status.declined
  ) {
    return { label: STATUS_LABELS[row.status] || row.status, tone: "closed" };
  }
  if (row.status === Constants.waitlist_status.refund_denied) return { label: STATUS_LABELS[row.status], tone: "denied" };
  return { label: STATUS_LABELS[row.status] || row.status, tone: "pending" };
}

const UserWaitlistTab: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const requestId = useRef(0);
  const [period, setPeriod] = useState<"upcoming" | "past">("upcoming");
  const [dogId, setDogId] = useState("");
  const [status, setStatus] = useState("");
  const [eventId, setEventId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pageData, setPageData] = useState<apiService.IUserEntriesPage>(emptyPage);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setFailed(false);
    apiService.searchUserEntries(props.user._id, {
      period,
      dog_id: dogId || undefined,
      event_id: eventId || undefined,
      status: status || undefined,
      $skip: (page - 1) * pageSize,
      $limit: pageSize,
      today: moment().format("YYYY-MM-DD"),
    }).then((response) => {
      if (id !== requestId.current) return;
      setPageData(response || emptyPage);
      setLoading(false);
    }).catch(() => {
      if (id !== requestId.current) return;
      setFailed(true);
      setLoading(false);
    });
  }, [dogId, eventId, page, pageSize, period, props.user._id, status]);

  useEffect(() => {
    load();
  }, [load]);

  const dogOptions: IOption[] = [
    ALL_DOGS,
    ...pageData.dogs.map((dog) => ({ id: dog.id, value: dog.call_name || "Dog" })),
  ];
  const statusOptions: IOption[] = [
    ALL_STATUSES,
    ...(pageData.statuses || []).map((id) => ({ id, value: STATUS_LABELS[id] || id })),
  ];
  const eventOptions: IOption[] = [
    ALL_EVENTS,
    ...pageData.events.map((event) => ({ id: event.id, value: event.title || "Event" })),
  ];
  const dogValue = dogOptions.find((option) => option.id === dogId) || ALL_DOGS;
  const statusValue = statusOptions.find((option) => option.id === status) || ALL_STATUSES;
  const eventValue = eventOptions.find((option) => option.id === eventId) || ALL_EVENTS;
  const filtersActive = Boolean(dogId || status || eventId);

  const onPeriod = (next: string) => {
    setPeriod(next === "past" ? "past" : "upcoming");
    setDogId("");
    setStatus("");
    setEventId("");
    setPage(1);
  };

  return (
    <div className={styles.UserWaitlistTab}>
      <SegmentedControl
        value={period}
        options={[
          { id: "upcoming", label: "Upcoming" },
          { id: "past", label: "Past" },
        ]}
        onChange={onPeriod}
      />

      <div className={styles.filters}>
        <div className={styles.field}>
          <Dropdown
            label="Dog"
            placeholder={ALL_DOGS.value}
            options={dogOptions}
            value={dogValue}
            onChange={(_event, option) => {
              setDogId(option.id);
              setPage(1);
            }}
          />
        </div>
        <div className={styles.field}>
          <Dropdown
            label="Status"
            placeholder={ALL_STATUSES.value}
            options={statusOptions}
            value={statusValue}
            onChange={(_event, option) => {
              setStatus(option.id);
              setPage(1);
            }}
          />
        </div>
        <div className={`${styles.field} ${styles.fieldWide}`}>
          <Dropdown
            label="Event or course"
            placeholder={ALL_EVENTS.value}
            searchPlaceholder="Search events and courses"
            searchable
            options={eventOptions}
            value={eventValue}
            onChange={(_event, option) => {
              setEventId(option.id);
              setPage(1);
            }}
          />
        </div>
        {filtersActive && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => {
              setDogId("");
              setStatus("");
              setEventId("");
              setPage(1);
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={pageData.total}
        pageSizeOptions={PAGE_SIZES}
        itemLabel="entries"
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      >
      {loading && <p className={styles.message}>Loading entries…</p>}
      {!loading && failed && <p className={styles.message}>Entries could not be loaded.</p>}
      {!loading && !failed && pageData.items.length === 0 && (
        <div className={styles.message}>
          <p>No entries found</p>
          {filtersActive && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                setDogId("");
                setStatus("");
                setEventId("");
                setPage(1);
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {!loading && !failed && pageData.items.length > 0 && (
        <div className={styles.list}>
          {pageData.items.map((row: any, index: number) => {
            const status = statusLabel(row);
            const kind = row.entry_kind || "event";
            const isEvent = kind === "event";
            const awaitingDraw = isEvent && row.status === Constants.waitlist_status.waitlist && isLotteryDrawOpen(row.event);
            const position = row.dog_class?.waitlist_position ?? row.position;
            const isCourseKind = kind === "course" || kind === "class" || kind === "seminar";
            const showPosition = row.status === Constants.waitlist_status.waitlist && !awaitingDraw && position != null;
            const showName = [row.show_type, row.show_name].filter(Boolean).join(" ");
            const sessions = Array.isArray(row.sessions) ? row.sessions : [];
            const classSession = sessions[0];
            const kindLabel = kind === "class" ? "Class" : kind === "seminar" ? "Seminar" : kind === "course" ? "Course" : "Event / Show";
            const kindLine = kind === "class"
              ? (classSession?.index > 0 ? `Session ${classSession.index}` : "Class")
              : kind === "seminar" ? "Seminar" : kind === "course" ? "Course" : showName;
            const listsSessions = kind === "course" || kind === "seminar";
            return (
              <article key={`${kind}-${row._id}-${row.dog_class?.show_element || ""}-${row.dog_class?.level || ""}-${index}`} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.identity}>
                    <div>
                      <p className={styles.kicker}>{kind === "seminar" ? "Name" : "Call name"}</p>
                      <h2>{row.call_name}</h2>
                    </div>
                    <span className={`${styles.badge} ${styles[status.tone]}`}>{status.label}</span>
                    {showPosition && <span className={styles.position}>Position #{position}</span>}
                  </div>
                  {(isEvent || isCourseKind) && period === "upcoming" && (
                    <div className={styles.actions}>
                      {row.status === Constants.waitlist_status.awaiting_payment && (
                        <button
                          type="button"
                          className={styles.pay}
                          onClick={() => navigate(isCourseKind ? `/c/${row.event?.handle}/register?waitlist=${row._id}` : `/e/${row.event?.handle}/waitlist-payment`)}
                        >
                          Make Payment
                        </button>
                      )}
                      {(isEvent || row.status === Constants.waitlist_status.waitlist) && (
                        <CancelRenderer entry={row} onChanged={load} />
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.details}>
                  <div className={styles.detail}>
                    <p className={styles.kicker}>{kindLabel}</p>
                    <Link className={styles.event} to={isEvent ? `/e/${row.event?.handle}` : `/c/${row.event?.handle}`}>{row.event?.title}</Link>
                    {kindLine && <p className={styles.show}>{kindLine}</p>}
                  </div>
                  {listsSessions ? (
                    <div className={styles.detail}>
                      <p className={styles.kicker}>Sessions</p>
                      <ul className={styles.sessions}>
                        {sessions.map((session: { index?: number; start_date?: string }, sessionIndex: number) => (
                          <li key={`${session.index || sessionIndex}-${session.start_date || sessionIndex}`}>{sessionLine(session)}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className={styles.detail}>
                      <p className={styles.kicker}>{kind === "class" ? "Session date" : "Show date"}</p>
                      <p className={styles.dateValue}>
                        <CalendarIcon />
                        <span>{row.show_date ? moment(row.show_date).format(kind === "class" ? "MMM D, YYYY, h:mm A" : "MMM D, YYYY") : ""}</span>
                      </p>
                    </div>
                  )}
                  {isEvent && (
                    <div className={styles.split}>
                      <div className={styles.detail}>
                        <p className={styles.kicker}>Element</p>
                        <p className={styles.value}>{row.dog_class?.show_element}</p>
                      </div>
                      <div className={styles.detail}>
                        <p className={styles.kicker}>Level</p>
                        <p className={styles.value}>{row.dog_class?.level}</p>
                      </div>
                    </div>
                  )}
                  <div className={styles.detail}>
                    <p className={styles.kicker}>Registered at</p>
                    <p className={styles.value}>{row.created_at ? moment(row.created_at).format("MMM D, YYYY [at] h:mm A") : ""}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
      </Pagination>
    </div>
  );
};

export default UserWaitlistTab;
